import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Gear, Sector, Stage, TOOL_NAMES } from '../domain/olmps';
import { makeThemedStyles } from '../v10/theme';
import { AkesoTool } from '../tools/AkesoTool';
import { ArgosTool } from '../tools/ArgosTool';
import { AstrsTool } from '../tools/AstrsTool';
import { ClioTool } from '../tools/ClioTool';
import { EpicTool } from '../tools/EpicTool';
import { HrmezTool } from '../tools/HrmezTool';
import { JnusTool } from '../tools/JnusTool';
import { KrnosTool } from '../tools/KrnosTool';
import { KyrsTool } from '../tools/KyrsTool';
import { MetisTool } from '../tools/MetisTool';
import { MoyriTool } from '../tools/MoyriTool';
import { NemosTool } from '../tools/NemosTool';
import { NumaTool } from '../tools/NumaTool';
import { OracleSpineTool } from './OracleSpineTool';
import { SofiaTool } from '../tools/SofiaTool';
import { SyqiTool } from '../tools/SyqiTool';
import { TelosTool } from '../tools/TelosTool';
import { ThemisTool } from '../tools/ThemisTool';
import { TkneTool } from '../tools/TkneTool';

export interface ToolProps {
  sector: Sector;
  stage: Stage;
  gear: Gear;
  /** Optional, additive preselection — currently only ThemisTool reads this
   *  (to open a specific protocol's runner on mount, from StepScreen's
   *  Execute cell). Every other tool ignores it; absent for every caller
   *  except that one code path. */
  initialProtocolId?: string;
  /** Optional, additive preselection — currently only KyrsTool reads this
   *  (to open/highlight a specific task on mount, from NUMA's "Open in
   *  KYRS" action). Absent for every other caller. */
  initialTaskId?: string;
  /** Optional cross-tool navigation — lets a tool open another tool in the
   *  shell (e.g. ORACLE's progress rings, which are links on web). Provided
   *  by OlmpsCore via ToolPanel; absent in contexts with no shell nav. */
  onOpenTool?: (toolId: string, opts?: { initialTaskId?: string }) => void;
}

// Mobile tool registry — native implementations only. Full web parity as of
// 2026-07-14 (see docs/mobile/PARITY.md). plutus/hygia/hypnos remain MOUNT
// placeholders because they are stubs on web itself.
const NATIVE_TOOLS: Record<string, React.ComponentType<ToolProps>> = {
  akeso: AkesoTool,
  argos: ArgosTool,
  astrs: AstrsTool,
  clio: ClioTool,
  epic: EpicTool,
  hrmez: HrmezTool,
  jnus: JnusTool,
  krnos: KrnosTool,
  kyrs: KyrsTool,
  metis: MetisTool,
  moyri: MoyriTool,
  nemos: NemosTool,
  numa: NumaTool,
  oracle: OracleSpineTool,
  sofia: SofiaTool,
  syqi: SyqiTool,
  telos: TelosTool,
  themis: ThemisTool,
  tkne: TkneTool,
};

class ToolErrorBoundary extends React.Component<
  { toolId: string; children: React.ReactNode },
  { hasError: boolean; message: string }
> {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, message: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown) {
    console.error(`Tool crashed: ${this.props.toolId}`, error);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorView toolId={this.props.toolId} message={this.state.message} />;
    }
    return this.props.children;
  }
}

function ErrorView({ toolId, message }: { toolId: string; message: string }) {
  const styles = useStyles();
  return (
        <View style={styles.center}>
          <Text style={styles.errorText}>Tool failed to load: {toolId}</Text>
          {message ? <Text style={styles.errorDetail}>{message}</Text> : null}
        </View>
  );
}

export function ToolHost({
  toolId,
  sector,
  stage,
  gear,
  initialProtocolId,
  initialTaskId,
  onOpenTool,
}: ToolProps & { toolId: string }) {
  const Native = NATIVE_TOOLS[toolId];
  const styles = useStyles();
  if (Native) {
    return (
      <ToolErrorBoundary toolId={toolId}>
        <Native
          sector={sector}
          stage={stage}
          gear={gear}
          initialProtocolId={initialProtocolId}
          initialTaskId={initialTaskId}
          onOpenTool={onOpenTool}
        />
      </ToolErrorBoundary>
    );
  }
  return (
    <View style={styles.center}>
      <Text style={styles.mountText}>MOUNT: {TOOL_NAMES[toolId] ?? toolId}</Text>
      <Text style={styles.mountHint}>Native version coming in a later slice</Text>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 6 },
    mountText: { fontSize: 16, fontWeight: '900', color: t.ivorySoft, letterSpacing: 1 },
    mountHint: { fontSize: 12, color: t.ivoryFaint, fontStyle: 'italic' },
    errorText: { color: t.lapsed, fontWeight: '700' },
    errorDetail: { color: t.lapsed, fontSize: 12, textAlign: 'center' },
  }),
);
